import { Encounter, Patient, Practitioner, CodeableConcept } from '@medplum/fhirtypes';
import { formatHumanName } from '@medplum/core';
import { HL7, ARKON } from '@consts/systems.consts';
import dayjs from 'dayjs';

/**
 * Form data structure for encounter creation
 */
export interface EncounterFormData {
  encounterType: CodeableConcept;
  date: Date;
  time: string;
  attendingDoctor?: Practitioner; // Optional for laboratory checks
  additionalDoctors: Practitioner[];
  reasonMode: 'text' | 'icd10';
  reasonText?: string;
  reasonCode?: CodeableConcept;
  referringDoctors: Practitioner[];
  additionalNotes?: string;
  showAdditionalNotes?: boolean;
}

/**
 * Builds a complete Encounter resource from form data
 *
 * @param patient - The patient for this encounter
 * @param formData - Form data containing encounter details
 * @returns Fully constructed Encounter resource ready for creation
 */
export function buildEncounterFromForm(
  patient: Patient,
  formData: EncounterFormData
): Encounter {
  const {
    encounterType,
    date,
    time,
    attendingDoctor,
    additionalDoctors,
    reasonMode,
    reasonText,
    reasonCode,
    referringDoctors,
    additionalNotes,
    showAdditionalNotes,
  } = formData;

  // Build ISO datetime string
  const startDateTime = `${dayjs(date).format('YYYY-MM-DD')}T${time}:00`;

  // Build participant array
  const participants: Encounter['participant'] = [];

  // Add attending doctor if provided
  if (attendingDoctor?.id) {
    participants.push({
      individual: {
        reference: `Practitioner/${attendingDoctor.id}`,
        display: formatHumanName(attendingDoctor.name?.[0]),
      },
      type: [
        {
          coding: [
            {
              system: HL7.TERMINOLOGY.CS.V3.PARTICIPATION_TYPE.BASE,
              code: HL7.TERMINOLOGY.CS.V3.PARTICIPATION_TYPE.CODES.ATND,
              display: 'attender',
            },
          ],
        },
      ],
    });
  }

  // Add additional doctors as consultants
  additionalDoctors
    .filter((p) => p?.id)
    .forEach((practitioner) => {
      participants.push({
        individual: {
          reference: `Practitioner/${practitioner.id}`,
          display: formatHumanName(practitioner.name?.[0]),
        },
        type: [
          {
            coding: [
              {
                system: HL7.TERMINOLOGY.CS.V3.PARTICIPATION_TYPE.BASE,
                code: HL7.TERMINOLOGY.CS.V3.PARTICIPATION_TYPE.CODES.CON,
                display: 'consultant',
              },
            ],
          },
        ],
      });
    });

  // Add referring doctors if selected
  referringDoctors
    .filter((p) => p?.id)
    .forEach((practitioner) => {
      participants.push({
        individual: {
          reference: `Practitioner/${practitioner.id}`,
          display: formatHumanName(practitioner.name?.[0]),
        },
        type: [
          {
            coding: [
              {
                system: HL7.TERMINOLOGY.CS.V3.PARTICIPATION_TYPE.BASE,
                code: HL7.TERMINOLOGY.CS.V3.PARTICIPATION_TYPE.CODES.REFERRER,
                display: 'referrer',
              },
            ],
          },
        ],
      });
    });

  // Build the encounter resource
  const encounter: Encounter = {
    resourceType: 'Encounter',
    status: 'in-progress',
    class: {
      system: HL7.TERMINOLOGY.CS.V3.ACT_CODE,
      code: 'AMB',
      display: 'ambulatory',
    },
    type: [encounterType],
    subject: {
      reference: `Patient/${patient.id}`,
      display: formatHumanName(patient.name?.[0]),
    },
    participant: participants,
    reasonCode: [reasonMode === 'text' ? { text: reasonText } : (reasonCode ?? {})],
    period: {
      start: startDateTime,
    },
    // Add notes as extension if provided
    ...(additionalNotes &&
      showAdditionalNotes && {
        extension: [
          {
            url: `${ARKON.BASE}/StructureDefinition/encounter-notes`,
            valueString: additionalNotes,
          },
        ],
      }),
  };

  return encounter;
}
